import { set } from 'lodash'
import { createContext, useContext, useEffect, useState } from 'react'

interface ChatbaseContextType {
  openChat: () => void
  closeChat: () => void
  isChatOpen: boolean
}

const ChatbaseContext = createContext<ChatbaseContextType | undefined>(
  undefined
)

export const useChatbase = () => {
  const context = useContext(ChatbaseContext)
  if (!context) {
    throw new Error('useChatbase must be used within ChatbaseProvider')
  }
  return context
}

interface ChatbaseProviderProps {
  children: React.ReactNode
}

// Add fade animation styles
const addFadeStyles = () => {
  if (typeof document === 'undefined') return

  const styleId = 'chatbase-fade-styles'
  if (document.getElementById(styleId)) return

  const style = document.createElement('style')
  style.id = styleId
  style.textContent = `
    @keyframes chatbaseFadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    @keyframes chatbaseFadeOut {
      from {
        opacity: 1;
      }
      to {
        opacity: 0;
      }
    }

    .chatbase-fade-in {
      animation: chatbaseFadeIn 0.3s ease-in-out forwards;
    }

    .chatbase-fade-out {
      animation: chatbaseFadeOut 0.3s ease-in-out forwards;
    }

    #chatbase-bubble-window {
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1) !important;
      margin-bottom: 16px !important;
    }
  `
  document.head.appendChild(style)
}

export const ChatbaseProvider = ({ children }: ChatbaseProviderProps) => {
  const [isChatOpen, setIsChatOpen] = useState(false)

  useEffect(() => {
    addFadeStyles()
  }, [])

  const openChat = () => {
    if (window.chatbase) {
      setIsChatOpen(true)

      // Add fade-in animation to chatbase window
      const chatbaseWindow = document.getElementById('chatbase-bubble-window')
      if (chatbaseWindow) {
        chatbaseWindow.classList.remove('chatbase-fade-out')
        chatbaseWindow.classList.add('chatbase-fade-in')
      }

      window.chatbase.open()
    }
  }

  const closeChat = () => {
    if (window.chatbase) {
      // Add fade-out animation to chatbase window
      const chatbaseWindow = document.getElementById('chatbase-bubble-window')
      if (chatbaseWindow) {
        chatbaseWindow.classList.remove('chatbase-fade-in')
        chatbaseWindow.classList.add('chatbase-fade-out')

        // Wait for animation to complete before closing
        setTimeout(() => {
          setIsChatOpen(false)
          window.chatbase.close()
        }, 300) // Match the animation duration
      } else {
        setIsChatOpen(false)
        window.chatbase.close()
      }
    }
  }

  return (
    <ChatbaseContext.Provider value={{ openChat, closeChat, isChatOpen }}>
      {children}
    </ChatbaseContext.Provider>
  )
}

interface ChatbaseCloseButtonProps {
  onClose: () => void
}
